<script lang="ts">
    import { cn } from '$lib/utils/cn';

    import type { HeaderSize } from '$lib/types/Components/SharedTypes';

    export let src = '';
    export let align = 'left';
    export let bgColor = 'transparent';
    export let size: HeaderSize = 'medium';
    export let showIcon = true;
    export let onClick: (event: MouseEvent | TouchEvent) => any;

    const logoSizeMap: Record<HeaderSize, number> = {
        small: 32,
        medium: 40,
        large: 48,
        xLarge: 64,
    };

    /**
     * Creates an IMGIX url with a specific height (/width) based on the
     * given size prop and the logoSizeMap.
     *
     * In case the given src has IMGIX crop attributes (focalpoint crop),
     * this method applies the cropping to the new URL as well.
     *
     * @param src Logo src/URL.
     * @param size Logo size.
     * @return Optimized logo src/URL with correct height and crop attributes if cropping is enabled.
     */
    export const optimizeLogoSrc = (src: string, size: HeaderSize): string => {
        const targetHeight: number | undefined = logoSizeMap[size];

        if (!targetHeight) {
            return src;
        }

        try {
            const srcUrl = new URL(src);
            const srcParams = new URLSearchParams(srcUrl.href);

            const optimizedUrl = new URL(srcUrl.origin + srcUrl.pathname);

            // These are the default parameters we add.
            optimizedUrl.searchParams.set('dpr', '2');
            optimizedUrl.searchParams.set('auto', 'compress,format');
            optimizedUrl.searchParams.set('h', targetHeight.toString());

            // Check for crop mode
            if (srcParams.get('fit') === 'crop' && srcParams.get('crop') === 'focalpoint') {
                const rawFpx = srcParams.get('fp-x');
                const rawFpy = srcParams.get('fp-y');
                const rawFpz = srcParams.get('fp-z');
                const rawH = srcParams.get('h');
                const rawW = srcParams.get('w');

                if (rawFpx && rawFpy && rawFpz && rawH && rawW) {
                    optimizedUrl.searchParams.set('fit', 'crop');
                    optimizedUrl.searchParams.set('crop', 'focalpoint');
                    optimizedUrl.searchParams.set('fp-x', rawFpx);
                    optimizedUrl.searchParams.set('fp-y', rawFpy);
                    optimizedUrl.searchParams.set('fp-z', rawFpz);

                    // We need to add a "w" attribute as well, otherwise the
                    // crop will not work as intended. We need to compute
                    // the downscaled/cropped width based on the targetHeight,
                    // original height, origin width and the crop "zoom" factor (fp-z).
                    const heightScaleFactor = targetHeight / parseFloat(rawH);
                    const scaledWidth = parseFloat(rawW) * heightScaleFactor;
                    const cropAdjustedWidth = scaledWidth * parseFloat(rawFpz);

                    optimizedUrl.searchParams.set('w', cropAdjustedWidth.toString());
                }
            }

            return optimizedUrl.href;
        } catch (error) {
            console.error(`Failed to optimize image src: ${error}`);
            return src;
        }
    };

    let alignClasses: string;
    let imageClasses: string;

    $: optimizedSrc = optimizeLogoSrc(src, size);

    $: alignClasses = cn({
        'fun-text-center': align === 'center',
        'fun-text-left': align === 'left',
    });

    $: imageClasses = cn('fun-inline-block', {
        'fun-h-8': size === 'small',
        'fun-h-10': size === 'medium',
        'fun-h-12': size === 'large',
        'fun-h-16': size === 'xLarge',
    });
</script>

<div class="fun-border-box fun-w-full fun-p-2" style="background-color: {bgColor}">
    <div class={alignClasses}>
        <button on:click={onClick} class={!showIcon ? 'fun-invisible' : ''}>
            <img class={imageClasses} src={optimizedSrc} alt="Logo" />
        </button>
    </div>
</div>
